import { motion } from 'motion/react'
import { SEO } from '@/lib/seo'
import { Badge } from '@/components/ui'
import { companyInfo } from '@/data/company'

export function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="UC Advisors Privacy Policy - Learn how we collect, use, and protect your personal information. CCPA compliant."
      />

      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <Badge className="mb-6">Legal</Badge>
            <h1 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-8">
              Privacy Policy
            </h1>

            <div className="prose prose-invert prose-slate max-w-none">
              <p className="text-slate-300 text-lg mb-8">
                Last updated: March 2026
              </p>

              <div className="space-y-8">
                {/* Introduction */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    Introduction
                  </h2>
                  <p className="text-slate-400 mb-4">
                    UC Advisors, a registered DBA of UC Advisers LLC ("we," "us," or "our"), respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, use our client portal, request assessments, communicate with us by SMS, or use our services.
                  </p>
                  <p className="text-slate-400">
                    By using our website, portal, or services, you consent to the practices described in this Privacy Policy. If you do not agree with our practices, please do not use our website, portal, or services.
                  </p>
                </section>

                {/* 1. Information We Collect */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    1. Information We Collect
                  </h2>
                  
                  <h3 className="text-lg font-semibold text-white mt-4 mb-3">1.1 Information You Provide</h3>
                  <p className="text-slate-400 mb-4">
                    We collect information you voluntarily provide when you:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Fill out contact forms or request assessments</li>
                    <li>Request a quote or consultation</li>
                    <li>Subscribe to our newsletter or communications</li>
                    <li>Engage us for consulting or managed services</li>
                    <li>Communicate with us via email, phone, or chat</li>
                  </ul>
                  <p className="text-slate-400 mb-4">
                    This information may include:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-6">
                    <li>Name, email address, phone number, and mailing address</li>
                    <li>Company name, job title, and industry</li>
                    <li>Information about your technology environment and needs</li>
                    <li>Payment information (processed securely through third-party providers)</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">1.2 Information Collected Automatically</h3>
                  <p className="text-slate-400 mb-4">
                    When you visit our website, we automatically collect certain information, including:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-6">
                    <li>IP address and approximate location</li>
                    <li>Browser type and version</li>
                    <li>Device type and operating system</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website or source</li>
                    <li>Click patterns and navigation behavior</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">1.3 UC Care Client Data</h3>
                  <p className="text-slate-400">
                    For UC Care managed services clients, we may access system configuration data, usage logs, and technical information necessary to provide support and monitoring services. This data is used solely for service delivery and is subject to confidentiality obligations in our service agreements.
                  </p>
                </section>

                {/* 2. How We Use Your Information */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    2. How We Use Your Information
                  </h2>
                  <p className="text-slate-400 mb-4">
                    We use the information we collect to:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2">
                    <li>Respond to your inquiries and provide requested services</li>
                    <li>Deliver and improve our consulting and managed services</li>
                    <li>Send you information about our products, services, and industry updates</li>
                    <li>Process payments and manage billing</li>
                    <li>Analyze website usage to improve user experience</li>
                    <li>Detect and prevent fraud or security threats</li>
                    <li>Comply with legal obligations and enforce our terms</li>
                    <li>Communicate important updates about your services</li>
                  </ul>
                </section>

                {/* 3. Information Sharing */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    3. Information Sharing
                  </h2>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">We do not sell your personal information.</strong> We may share your information in the following circumstances:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-3">
                    <li><strong className="text-white">Service Providers:</strong> We share information with trusted third-party service providers who assist us in operating our business, including hosting providers (Cloudflare), CRM systems (HubSpot), and payment processors.</li>
                    <li><strong className="text-white">Technology Vendors:</strong> With your consent, we may share information with technology vendors (Zoom, Cisco, etc.) as necessary to facilitate services you have requested.</li>
                    <li><strong className="text-white">Legal Requirements:</strong> We may disclose information if required by law, regulation, legal process, or government request.</li>
                    <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
                    <li><strong className="text-white">Protection of Rights:</strong> We may disclose information to protect the rights, property, or safety of UC Advisors, our clients, or others.</li>
                  </ul>
                </section>

                {/* 4. Third-Party Service Providers */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    4. Third-Party Service Providers
                  </h2>
                  <p className="text-slate-400 mb-4">
                    We use the following categories of third-party service providers:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-3">
                    <li><strong className="text-white">Website Hosting:</strong> Cloudflare provides hosting, CDN, and security services for our website.</li>
                    <li><strong className="text-white">CRM and Marketing:</strong> HubSpot manages our customer relationship data and marketing communications.</li>
                    <li><strong className="text-white">Analytics:</strong> We may use analytics services to understand website usage patterns.</li>
                    <li><strong className="text-white">Payment Processing:</strong> Secure payment processors handle billing transactions.</li>
                  </ul>
                  <p className="text-slate-400 mt-4">
                    These providers are contractually obligated to protect your information and use it only for the services they provide to us.
                  </p>
                </section>

                {/* 5. Data Retention */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    5. Data Retention
                  </h2>
                  <p className="text-slate-400 mb-4">
                    We retain your personal information for as long as necessary to:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Provide services you have requested</li>
                    <li>Comply with legal obligations (e.g., tax and accounting requirements)</li>
                    <li>Resolve disputes and enforce our agreements</li>
                    <li>Maintain business records for legitimate purposes</li>
                  </ul>
                  <p className="text-slate-400">
                    When you cancel services or request deletion, we will delete or anonymize your personal information within 90 days, except where retention is required by law or for legitimate business purposes.
                  </p>
                </section>

                {/* 6. Data Security */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    6. Data Security
                  </h2>
                  <p className="text-slate-400 mb-4">
                    We implement appropriate technical and organizational measures to protect your personal information, including:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Encryption of data in transit (TLS/SSL) and at rest</li>
                    <li>Access controls and authentication requirements</li>
                    <li>Regular security assessments and monitoring</li>
                    <li>Employee training on data protection practices</li>
                  </ul>
                  <p className="text-slate-400">
                    While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                  </p>
                </section>

                {/* 7. Data Breach Notification */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    7. Data Breach Notification
                  </h2>
                  <p className="text-slate-400">
                    In the event of a data breach that affects your personal information, we will notify you in accordance with applicable laws. For California residents, notification will be provided within 72 hours of discovery if the breach is reasonably likely to result in harm. Notification will include the nature of the breach, types of information affected, and steps you can take to protect yourself.
                  </p>
                </section>

                {/* 8. California Privacy Rights (CCPA) */}
                <section className="glass-card p-8 rounded-2xl border-2 border-accent-500/30">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    8. California Privacy Rights (CCPA/CPRA)
                  </h2>
                  <p className="text-slate-400 mb-4">
                    If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA):
                  </p>
                  
                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">Your Rights</h3>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-6">
                    <li><strong className="text-white">Right to Know:</strong> You can request information about the categories and specific pieces of personal information we have collected about you.</li>
                    <li><strong className="text-white">Right to Delete:</strong> You can request deletion of your personal information, subject to certain exceptions.</li>
                    <li><strong className="text-white">Right to Correct:</strong> You can request correction of inaccurate personal information.</li>
                    <li><strong className="text-white">Right to Opt-Out:</strong> You can opt out of the sale or sharing of your personal information.</li>
                    <li><strong className="text-white">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">Do Not Sell or Share My Personal Information</h3>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">UC Advisors does not sell your personal information.</strong> We do not exchange your personal information for monetary consideration.
                  </p>
                  <p className="text-slate-400 mb-4">
                    We may share certain information with analytics and advertising partners for targeted advertising purposes, which may constitute "sharing" under CCPA. You can opt out of this sharing by:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-6">
                    <li>Clicking "Decline All" or managing preferences in our cookie consent banner</li>
                    <li>Emailing us at privacy@ucadvisors.com with subject "Do Not Share"</li>
                    <li>Using Global Privacy Control (GPC) signals in your browser</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">How to Exercise Your Rights</h3>
                  <p className="text-slate-400">
                    To exercise your California privacy rights, contact us at privacy@ucadvisors.com or call {companyInfo.phone}. We will verify your identity before processing your request and respond within 45 days as required by law.
                  </p>
                </section>

                {/* 9. Cookies */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    9. Cookies and Tracking Technologies
                  </h2>
                  <p className="text-slate-400 mb-4">
                    We use cookies and similar tracking technologies to collect information about your browsing activities. You can manage your cookie preferences through our cookie consent banner or your browser settings.
                  </p>
                  <p className="text-slate-400">
                    For detailed information about the cookies we use and how to manage them, please see our <a href="/cookies" className="text-accent-400 hover:underline">Cookie Policy</a>.
                  </p>
                </section>

                {/* 10. SMS/Text Messaging Consent Policy */}
                <section className="glass-card p-8 rounded-2xl border-2 border-emerald-500/30">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    10. SMS/Text Messaging Consent Policy
                  </h2>
                  <p className="text-slate-400 mb-4">
                    UC Advisors may use SMS (Short Message Service) and MMS (Multimedia Message Service) text messaging to communicate with clients and prospects who have provided consent. This section describes how we collect consent, use text messaging, and your rights regarding text communications.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.1 Consent Requirements</h3>
                  <p className="text-slate-400 mb-4">
                    By providing your mobile phone number and opting in to receive text messages from UC Advisors, you expressly consent to receive recurring automated marketing and informational text messages at the phone number provided. Your consent is not a condition of purchase.
                  </p>
                  <p className="text-slate-400 mb-4">
                    Consent may be obtained through:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Website forms where you check a box agreeing to receive text messages</li>
                    <li>Written consent on service agreements or contracts</li>
                    <li>Verbal consent documented by our team</li>
                    <li>Replying "YES" or an affirmative keyword to an opt-in request</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.2 Types of Messages</h3>
                  <p className="text-slate-400 mb-4">
                    UC Advisors may send text messages for the following purposes:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li><strong className="text-white">Service Notifications:</strong> Updates about your UC Care services, appointments, and support tickets</li>
                    <li><strong className="text-white">Account Alerts:</strong> Important information about your account, billing, or service changes</li>
                    <li><strong className="text-white">Promotional Messages:</strong> Information about new services, assessments, and special offers (with your consent)</li>
                    <li><strong className="text-white">Appointment Reminders:</strong> Confirmations and reminders for scheduled consultations</li>
                    <li><strong className="text-white">Two-Factor Authentication:</strong> Security codes for account access</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.3 Message Frequency</h3>
                  <p className="text-slate-400 mb-4">
                    Message frequency varies based on the type of communication and your service level. Typically, you may receive:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Service notifications: As needed based on your account activity</li>
                    <li>Promotional messages: Up to 4 messages per month</li>
                    <li>Appointment reminders: 1-2 messages per scheduled appointment</li>
                  </ul>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.4 Opt-Out Instructions</h3>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">You may opt out of text messages at any time.</strong> To stop receiving text messages from UC Advisors:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Reply <strong className="text-white">STOP</strong>, <strong className="text-white">END</strong>, <strong className="text-white">CANCEL</strong>, <strong className="text-white">UNSUBSCRIBE</strong>, or <strong className="text-white">QUIT</strong> to any text message</li>
                    <li>Email us at privacy@ucadvisors.com with "Unsubscribe SMS" in the subject line</li>
                    <li>Call us at {companyInfo.phone} to request removal</li>
                    <li>Log into the UC Advisors Onboarding Portal and update your communication preferences</li>
                  </ul>
                  <p className="text-slate-400 mb-4">
                    After opting out, you will receive a confirmation message. Please allow up to 48 hours for your request to be fully processed. Opting out of promotional messages will not affect service-related or transactional messages necessary for your account.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.5 Help and Support</h3>
                  <p className="text-slate-400 mb-4">
                    For help with text messaging, reply <strong className="text-white">HELP</strong> to any message or contact us at {companyInfo.phone} or support@ucadvisors.com.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.6 Costs and Carrier Information</h3>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-white">Message and data rates may apply.</strong> Standard messaging rates from your wireless carrier apply to messages sent and received. UC Advisors does not charge for text messages, but your carrier may. Check with your mobile carrier for details about your messaging plan.
                  </p>
                  <p className="text-slate-400 mb-4">
                    Text messaging services are supported by most major carriers including AT&T, Verizon, T-Mobile, Sprint, and others. Carriers are not liable for delayed or undelivered messages.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.7 Delivery and Limitations</h3>
                  <p className="text-slate-400 mb-4">
                    Text messages are delivered on a "best efforts" basis. UC Advisors utilizes third-party messaging service providers and carriers to deliver messages. We cannot guarantee:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Delivery of all messages or delivery within any specific timeframe</li>
                    <li>Message delivery to all wireless carriers or devices</li>
                    <li>That the service will be uninterrupted or error-free</li>
                  </ul>
                  <p className="text-slate-400 mb-4">
                    The Text Messaging Service may not be available in all areas or on all devices. Zoom Phone or other underlying service providers may impose limitations on message delivery.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.8 Information We Collect via Text Messaging</h3>
                  <p className="text-slate-400 mb-4">
                    When you opt in to text messaging, we collect:
                  </p>
                  <ul className="list-disc list-inside text-slate-400 space-y-2 mb-4">
                    <li>Your mobile phone number</li>
                    <li>Consent timestamp and method</li>
                    <li>Message delivery status and engagement metrics</li>
                    <li>Opt-out requests and dates</li>
                  </ul>
                  <p className="text-slate-400 mb-4">
                    <strong className="text-accent-400">We do not sell, rent, or share your phone number or text messaging consent information with third parties for their marketing purposes.</strong> Your information may be shared with our messaging service providers solely for the purpose of delivering messages.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.9 Consent Records</h3>
                  <p className="text-slate-400 mb-4">
                    We maintain records of your consent for a minimum of four (4) years or as required by applicable laws. Consent records include the timestamp of consent, the method of consent acquisition, the specific messaging program consented to, and the phone number provided.
                  </p>

                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">10.10 Compliance</h3>
                  <p className="text-slate-400">
                    UC Advisors' text messaging practices comply with the Telephone Consumer Protection Act (TCPA), Cellular Telecommunications Industry Association (CTIA) guidelines, and applicable state and federal regulations. We register all messaging campaigns with The Campaign Registry as required by major wireless carriers.
                  </p>
                </section>

                {/* 11. International Data */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    11. International Data Processing
                  </h2>
                  <p className="text-slate-400">
                    UC Advisors is based in the United States, and your information is processed and stored in the United States. If you are accessing our website or services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States, where data protection laws may differ from those in your country.
                  </p>
                </section>

                {/* 12. Children's Privacy */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    12. Children's Privacy
                  </h2>
                  <p className="text-slate-400">
                    Our website and services are not intended for children under 16 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately so we can delete it.
                  </p>
                </section>

                {/* 13. Changes to This Policy */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    13. Changes to This Policy
                  </h2>
                  <p className="text-slate-400">
                    We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will post the updated policy on this page with a new "Last updated" date. For material changes, we will provide notice through our website or by email. Your continued use of our website or services after changes constitutes acceptance of the updated policy.
                  </p>
                </section>

                {/* 14. Contact Us */}
                <section className="glass-card p-8 rounded-2xl">
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    14. Contact Us
                  </h2>
                  <p className="text-slate-400 mb-4">
                    If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
                  </p>
                  <div className="text-slate-300 space-y-1">
                    <p className="font-semibold">{companyInfo.name}</p>
                    <p>{companyInfo.locations[0].address}</p>
                    <p>{companyInfo.locations[0].city}, {companyInfo.locations[0].state}</p>
                    <p>Email: privacy@ucadvisors.com</p>
                    <p>Phone: {companyInfo.phone}</p>
                  </div>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
